import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { HouseLine, HardHat, ArrowRight } from "@phosphor-icons/react";

const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_pro-connect-315/artifacts/ws4b3em5_1.png";

const RoleCard = ({ icon: Icon, title, description, cta, onClick, testId, accent }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="group relative flex h-full flex-col overflow-hidden rounded-md border border-[#E5E2DB] bg-white p-8 text-left transition-all duration-300 hover:border-[#0A0F0D] hover:shadow-brutal md:p-10"
  >
    <div
      className={`mb-6 inline-flex h-14 w-14 items-center justify-center rounded-md ${
        accent ? "bg-[#C65D47]" : "bg-[#0A0F0D]"
      }`}
    >
      <Icon size={28} weight="duotone" color="#FFFFFF" />
    </div>
    <h3 className="font-display text-2xl font-bold tracking-tight text-[#0A0F0D] md:text-3xl">
      {title}
    </h3>
    <p className="mt-3 max-w-sm text-base leading-relaxed text-[#5C605E]">{description}</p>
    <div className="mt-auto flex items-center gap-2 pt-8 text-sm font-semibold text-[#0A0F0D] group-hover:text-[#C65D47]">
      <span>{cta}</span>
      <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
    </div>
  </button>
);

const RoleSelectionPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();

  const choose = async (role) => {
    // Anonymous flow: send them to auth with the chosen role in the URL
    if (!user) {
      navigate(`/auth?role=${role}`);
      return;
    }

    // Authenticated flow: set the role now
    setSubmitting(true);
    try {
      await api.post("/users/role", { role });
      await refreshUser();
      if (role === "client") navigate("/client/categories");
      else navigate("/contractor/register");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  const heading = user
    ? (
      <>
        How will you use ConnectAPro,{" "}
        <span className="text-[#C65D47]">{user?.name?.split(" ")[0] || "there"}</span>?
      </>
    )
    : (
      <>
        Which side of ConnectAPro
        <br />
        <span className="text-[#C65D47]">are you on?</span>
      </>
    );

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="role-selection-page">
      <Toaster position="top-right" richColors />
      <Header />
      <main className="mx-auto max-w-6xl px-6 pb-16 pt-8 md:px-10 md:pb-24 md:pt-12">
        {/* Logo — frameless, aligned with the auth page treatment */}
        <div className="flex justify-center">
          <img
            src={LOGO_URL}
            alt="Connect A Pro"
            className="h-40 w-auto object-contain sm:h-48"
            data-testid="role-selection-logo"
          />
        </div>

        <div className="mx-auto mt-6 max-w-2xl animate-fade-up text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#C65D47]">
            {user ? "Step 01 / 03" : "Get started"}
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-[#0A0F0D] sm:text-5xl lg:text-6xl">
            {heading}
          </h1>
          <p className="mt-5 text-base leading-relaxed text-[#5C605E]">
            {user
              ? "Pick the option that describes you best. You can change this later from your profile."
              : "Pick the option that describes you best — we'll take you straight to sign up or log in."}
          </p>
        </div>

        <div className="mt-14 grid gap-8 md:grid-cols-2">
          <RoleCard
            icon={HouseLine}
            title="I need a pro"
            description="Post a job, upload photos, pin your location and get matched with vetted local contractors ready to help."
            cta="Continue as Client"
            testId="role-client-card"
            onClick={() => !submitting && choose("client")}
          />
          <RoleCard
            icon={HardHat}
            title="I am a pro"
            description="Register your trade — electrician, plumber, handyman, gardener or general — and grow your local client base."
            cta="Continue as Contractor"
            testId="role-contractor-card"
            accent
            onClick={() => !submitting && choose("contractor")}
          />
        </div>
      </main>
    </div>
  );
};

export default RoleSelectionPage;
